import BaseCtrl from 'edge/platform/base/BaseCtrl';
import {PhoneNumberUtil} from 'libphonenumber';
import ApplicationFeatures from 'gw-portals-featuredetection-js/ApplicationFeatures';
import nameUtil from 'edge/gateway/util/NameUtil';

const fn = (
    $scope, $state, lobConfig, $window, $q, $stateParams, CancellationService, ActivityService, $modal,
    ModalService, $filter, UserService, $translate, InsuranceSuiteMetadata, defaultLocaleDetails
) => {

    const ctrl = Object.create(BaseCtrl);

    const phoneUtils = PhoneNumberUtil.getInstance();

    ctrl.init = () => {
        const applicationFeatures = ApplicationFeatures.getAllFeatures();
        $scope.gatewayQuote = applicationFeatures.gatewayquoteandbind === 'true';
        $scope.cancellationNumber = $stateParams.cancellationNumber;

        $scope.loadingCancellationPromise = CancellationService.findJobByJobNumber($stateParams.cancellationNumber).then(
            (cancellation) => {
                if (!cancellation.statusCode) {
                    ModalService.showError(
                        'gateway.directives.JobSummary.Error',
                        'gateway.directives.JobSummary.Error.Details'
                    );
                    $state.go('policies.index');
                }

                $scope.accountInfo = {};

                mapCancellationDetails(cancellation);
                mapAccountDetails(cancellation.policy.account);

                if ($scope.cancellation.statusCode === 'NotTaken') {
                    $scope.isNotTaken = true;
                }
            },
            () => {
                ModalService.showError(
                    'gateway.directives.JobSummary.Error',
                    'gateway.directives.JobSummary.Error.Details'
                );
                $state.go('policies.index');
            }
        );

        updateSummaryFromServer();

        $scope.$watchGroup(['bindCancellationPromise', 'withdrawCancellationPromise'], () => {
            $scope.cancellationActionPromise = $q.all([$scope.bindCancellationPromise, $scope.withdrawCancellationPromise]);
        });
    };

    function mapAccountDetails(accountDetails) {
        if (!accountDetails) {
            return;
        }

        $scope.accountInfo.publicID = accountDetails.publicID;
        $scope.accountInfo.policySummaries = accountDetails.policySummaries;
        $scope.accountInfo.accountNumber = accountDetails.accountNumber;
        $scope.accountInfo.canCreateSub = accountDetails.canUserCreateCancellation;

        setupAccountContactDetails(accountDetails.accountHolder);
    }

    function setupAccountContactDetails(accountHolder) {
        $scope.accountInfo.subtype = accountHolder.subtype;
        if (accountHolder.subtype === 'Person') {
            $scope.accountInfo.firstName = accountHolder.firstName;
            $scope.accountInfo.lastName = accountHolder.lastName;
            $scope.accountInfo.contactName = nameUtil.getDisplayName(accountHolder, ' ');
        } else {
            $scope.accountInfo.contactName = accountHolder.contactName;
        }

        $scope.accountInfo.primaryAddress = accountHolder.primaryAddress;

        if (accountHolder.primaryPhoneType === 'work' && angular.isDefined(accountHolder.workNumber) && accountHolder.workNumber.length > 0) {
            $scope.accountInfo.primaryPhoneNumber = phoneUtils.format(phoneUtils.parse(accountHolder.workNumber, defaultLocaleDetails.countryCode));
        } else if (accountHolder.primaryPhoneType === 'home' && angular.isDefined(accountHolder.homeNumber) && accountHolder.homeNumber.length > 0) {
            $scope.accountInfo.primaryPhoneNumber = phoneUtils.format(phoneUtils.parse(accountHolder.homeNumber, defaultLocaleDetails.countryCode));
        } else if (accountHolder.primaryPhoneType === 'mobile' && angular.isDefined(accountHolder.cellNumber) && accountHolder.cellNumber.length > 0) {
            $scope.accountInfo.primaryPhoneNumber = phoneUtils.format(phoneUtils.parse(accountHolder.cellNumber, defaultLocaleDetails.countryCode));
        }
    }

    function mapCancellationDetails(cancellation) {
        $scope.cancellation = cancellation;
        $scope.latestPeriod = cancellation.policy.latestPeriod;
        $scope.policyNumber = cancellation.policy.policyNumber;
        $scope.createTime = cancellation.createTime;
        $scope.cancellationReason = InsuranceSuiteMetadata.get('pc').types.getTypelist('ReasonCode').getCode(cancellation.cancelReasonCode).name;
        $scope.cancellationRefundMethod = InsuranceSuiteMetadata.get('pc').types.getTypelist('CalculationMethod').getCode(cancellation.cancellationRefundMethod).name;
        $scope.cancellationIsDraft = cancellation.status === InsuranceSuiteMetadata.get('pc').types.getTypelist('PolicyPeriodStatus').getCode('Draft').name;
        $scope.cancellationIsCancelling = cancellation.status === InsuranceSuiteMetadata.get('pc').types.getTypelist('PolicyPeriodStatus').getCode('Canceling').name;

        if ($scope.cancellation.isRefund) {
            $scope.cancellation.changeInCost.amount *= -1;
        }

        if (!$scope.cancellation.description) {
            $scope.cancellation.description = '-';
        }
    }

    ctrl.bindCancellation = (cancellationNumber) => {
        const message = $translate('gateway.views.cancellation-detail.You won\'t be able to undo once cancelled.')
            .then((msg) => {
                return $translate('gateway.views.cancellation-detail.Are you sure you want to cancel this policy?')
                    .then((secondMsg) => {
                        return `${msg} ${secondMsg}`;
                    });
            });
        const modalPromise = ModalService.showConfirm(
            'gateway.views.cancellation-detail.Bind Cancellation',
            message,
            undefined,
            $filter('translate')('gateway.views.cancellation-detail.Yes'),
            undefined,
            $filter('translate')('gateway.views.cancellation-detail.No')
        ).result;

        modalPromise.then(() => {
            $scope.bindCancellationPromise = CancellationService.bindCancellation(cancellationNumber).then(
                (cancellation) => {
                    mapCancellationDetails(cancellation);
                },
                () => {
                    ModalService.showError('gateway.views.modal.Error', 'gateway.views.cancellation-detail.Failed to bind cancellation');
                }
            );
        });
    };

    ctrl.withdrawCancellationByCancellationNumber = (cancellationNumber) => {
        const modalPromise = ModalService.showConfirm(
            'gateway.views.cancellation-detail.Withdraw Cancellation',
            $translate('gateway.views.cancellation-detail.Are you sure you want to withdraw the cancellation?'),
            undefined,
            $filter('translate')('gateway.views.cancellation-detail.Withdraw Cancellation'),
            undefined,
            $filter('translate')('gateway.views.cancellation-detail.Do Not Withdraw')
        ).result;

        modalPromise.then(() => {
            $scope.withdrawCancellationPromise = CancellationService
                .withdrawCancellationByCancellationNumber(cancellationNumber)
                .then((cancellation) => {
                    mapCancellationDetails(cancellation);
                }, () => {
                    ModalService.showError('gateway.views.modal.Error', 'gateway.views.cancellation-detail.Failed to withdraw cancellation');
                });
        });
    };

    function updateSummaryFromServer() {
        $scope.cancellationSummaryRequestPromise = CancellationService
            .getCancellationSummary($stateParams.cancellationNumber)
            .then(
                (cancellationSummary) => {
                    $scope.cancellationSummary = cancellationSummary;
                },
                () => {
                }
            );
    }

    $scope.$on('notesUpdated', () => {
        updateSummaryFromServer();
    });

    $scope.$on('documentsUpdated', () => {
        updateSummaryFromServer();
    });

    $scope.$on('activitiesUpdated', () => {
        updateSummaryFromServer();
    });

    return ctrl.create($scope);
};
fn.$inject = ['$scope', '$state', 'lobConfig', '$window', '$q', '$stateParams', 'CancellationService', 'ActivityService', '$modal', 'ModalService', '$filter', 'UserService', '$translate', 'InsuranceSuiteMetadata', 'defaultLocaleDetails'];

export default fn;
